import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Briefcase,
  Boxes,
  Wrench,
  UsersRound,
  LogOut,
  CircleDot,
  Wallet,
  ClipboardCheck,
  ClipboardList,
  UserCircle,
  Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";

/**
 * NAV layout:
 *   - Dashboard: visible to everyone
 *   - My Assignments / Team Assignments: visible to all roles
 *   - Module-based pages (CDARK, Projects, Assets, Services, Tasks, Tickets, OPA Handle):
 *       visible when the module is enabled for any team the user belongs to.
 *       Admins see everything regardless.
 *   - Strictly-admin pages (Approvals, Teams/Users/Wallets): adminOnly.
 *
 * Each nav row may carry a `module` key — when present we additionally hide the
 * row if the user's effective enabled-modules list (from /api/me/modules) does
 * not include it. Admins always see everything.
 */
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", module: "dashboard" },
  { to: "/assignments", label: "Assignments", icon: ClipboardList, testid: "nav-assignments", anyOf: ["tasks", "issues"] },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck, testid: "nav-approvals", approvalsGated: true },
  { to: "/projects", label: "CDARK", icon: FolderKanban, testid: "nav-projects", module: "projects" },
  { to: "/biz-projects", label: "Projects", icon: Briefcase, testid: "nav-biz-projects", module: "biz_projects" },
  { to: "/assets", label: "Assets", icon: Boxes, testid: "nav-assets", module: "assets" },
  { to: "/services", label: "Services", icon: Wrench, testid: "nav-services", module: "services" },
  { to: "/opa", label: "OPA Handle", icon: Wallet, testid: "nav-opa", module: "opa_requests" },
  { to: "/my-wallet", label: "Wallets", icon: Wallet, testid: "nav-wallets", module: "wallets" },
  { to: "/teams-users", label: "Teams, Users & Wallets", icon: UsersRound, testid: "nav-teams-users", module: "teams_users_wallets" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", module: "settings" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ modules: null, role: user?.role });
  const [settings, setSettings] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const logoToken = typeof window !== "undefined" ? localStorage.getItem("ops_token") : null;

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/modules")
      .then(({ data }) => { if (!cancelled) setMeta(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // Org settings — refresh whenever the Settings page dispatches `settings-updated`.
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/settings")
      .then(({ data }) => { if (!cancelled) setSettings(data); })
      .catch(() => {});
    load();
    window.addEventListener("settings-updated", load);
    return () => { cancelled = true; window.removeEventListener("settings-updated", load); };
  }, [user?.id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const role = meta.role || user?.role;
  const isAdmin = role === "admin";
  const enabled = meta.modules; // null/undefined while loading
  const canViewApprovals = meta.can_view_approvals;

  const visibleNav = NAV.filter((n) => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.approvalsGated) {
      if (canViewApprovals === undefined) return isAdmin;
      return canViewApprovals;
    }
    if (n.module && !isAdmin && enabled && !enabled.includes(n.module)) return false;
    // `anyOf`: show only when at least one of the listed modules is enabled.
    // Admin always sees them.
    if (n.anyOf && !isAdmin && enabled && !n.anyOf.some((m) => enabled.includes(m))) return false;
    return true;
  });

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100">
      <aside
        className="w-60 shrink-0 border-r border-white/10 bg-zinc-950 flex flex-col"
        data-testid="app-sidebar"
      >
        <Link
          to="/"
          className="px-5 py-6 border-b border-white/10 block hover:bg-white/[0.03] transition-colors"
          data-testid="sidebar-brand-link"
          title="Go to dashboard"
        >
          <div className="flex items-center gap-2">
            {settings?.logo_file?.id ? (
              <img
                src={`${backendUrl}/api/files/${settings.logo_file.id}/download?auth=${logoToken}`}
                alt={settings.organization_name || "Logo"}
                className="w-7 h-7 rounded-md object-contain bg-white/5 border border-white/10"
                data-testid="sidebar-brand-logo"
              />
            ) : (
              <div className="w-7 h-7 rounded-md bg-white text-black flex items-center justify-center font-semibold">
                <CircleDot className="w-4 h-4" strokeWidth={2} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-semibold tracking-tight truncate" style={{ fontFamily: "Outfit" }} data-testid="sidebar-brand-name">
                {settings?.organization_name || "Digi-One"}
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-[0.18em]">
                Console
              </div>
            </div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-white text-black font-medium"
                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `block px-3 py-2 mb-2 rounded-md transition-colors ${isActive ? "bg-white/5" : "hover:bg-white/5"}`
            }
            data-testid="sidebar-profile-link"
          >
            <div className="text-[10px] text-zinc-500 uppercase tracking-[0.18em] mb-1">
              Signed in · <span className="capitalize">{(role || "team_associate").replace(/_/g, " ")}</span>
            </div>
            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">
              {user?.name || user?.email}
            </div>
            <div className="text-xs text-zinc-500 truncate flex items-center gap-1.5">
              <UserCircle className="w-3 h-3" /> View profile
            </div>
          </NavLink>
          <Button
            variant="ghost"
            className="w-full justify-start text-zinc-300 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="app-main">
          {children}
        </div>
      </main>

      <Toaster theme="dark" position="top-right" />
    </div>
  );
}
